import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

export default function TeslaWrapSanDiegoPage() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>Tesla Wrap San Diego | Model 3, Model Y Color Change Wraps | Mr Wrap</title>
        <meta name="description" content="Expert Tesla wrap services in San Diego. Color change wraps for Model 3, Model Y, Model S & Model X. Professional installation, premium vinyl. Get a free quote today!" />
        <link rel="canonical" href="https://autowrapsandiego.com/tesla-wrap-san-diego" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Tesla Wrap Specialists in San Diego
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Transform your Tesla with premium color change wraps. Expert installation for Model 3, Model Y, Model S, and Model X in San Diego and North County. Looking for wraps on another vehicle? See our <Link to="/car-wrap-san-diego" className="text-cyan-400 hover:underline">car wrap services in San Diego</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:858-282-8119"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg transition text-lg"
              >
                Call 858-282-8119
              </a>
              <Link
                to="/contact"
                className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-4 px-8 rounded-lg transition text-lg"
              >
                Get Free Quote
              </Link>
            </div>
          </div>
        </section>

        {/* Why Tesla Owners Choose Wraps */}
        <section className="py-16 px-6 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Why Tesla Owners Choose Color Change Wraps
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-800/50 p-8 rounded-lg border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">Paint Protection</h3>
                <p className="text-gray-300">
                  Preserve your Tesla's factory paint while adding a custom look. Wraps protect against rock chips, scratches, and UV damage—perfect for maintaining resale value.
                </p>
              </div>
              <div className="bg-gray-800/50 p-8 rounded-lg border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">Reversible Customization</h3>
                <p className="text-gray-300">
                  Change your Tesla's color without permanent modifications. Wraps can be removed to return to original paint, making them ideal for leased vehicles.
                </p>
              </div>
              <div className="bg-gray-800/50 p-8 rounded-lg border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">Unique Appearance</h3>
                <p className="text-gray-300">
                  Stand out with matte, gloss, satin, or chrome finishes in any color. From subtle elegance to bold statements—unlimited design possibilities.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tesla Model Wraps */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Professional Wraps for Every Tesla Model
            </h2>

            <div className="space-y-12">
              {/* Model 3 */}
              <div className="bg-gray-800/30 p-8 rounded-lg border border-gray-700">
                <h3 className="text-3xl font-bold text-white mb-4">Tesla Model 3 Wraps</h3>
                <p className="text-gray-300 mb-6">
                  The Model 3 is Tesla's most popular vehicle, and color change wraps are the perfect way to personalize it. Whether you want a sleek matte black finish, a vibrant racing red, or a sophisticated satin gray, we offer full and partial wraps tailored to your style. Our precision installation ensures seamless coverage around the Model 3's aerodynamic curves and door handles.
                </p>
                <p className="text-gray-300 mb-6">
                  <strong className="text-cyan-400">Popular Model 3 wrap options:</strong> Matte black, satin white, gloss metallic blue, chrome delete accents, two-tone designs.
                </p>
                <p className="text-gray-400 italic">
                  Starting at $2,500 for full color change wraps
                </p>
              </div>

              {/* Model Y */}
              <div className="bg-gray-800/30 p-8 rounded-lg border border-gray-700">
                <h3 className="text-3xl font-bold text-white mb-4">Tesla Model Y Wraps</h3>
                <p className="text-gray-300 mb-6">
                  The Model Y's larger surface area makes it a stunning canvas for color change wraps. From understated elegance to head-turning bold colors, we handle every detail with precision. Our wraps are designed to withstand San Diego's sun and coastal elements while maintaining a flawless finish.
                </p>
                <p className="text-gray-300 mb-6">
                  <strong className="text-cyan-400">Popular Model Y wrap options:</strong> Satin grey, glossy deep blue, matte military green, brushed metal finishes, custom graphics.
                </p>
                <p className="text-gray-400 italic">
                  Starting at $2,800 for full color change wraps
                </p>
              </div>

              {/* Model S */}
              <div className="bg-gray-800/30 p-8 rounded-lg border border-gray-700">
                <h3 className="text-3xl font-bold text-white mb-4">Tesla Model S Wraps</h3>
                <p className="text-gray-300 mb-6">
                  The Model S deserves a premium finish. Our color change wraps enhance the sedan's elegant lines with high-quality vinyl that delivers showroom perfection. Whether you prefer classic sophistication or a bold statement, we offer wraps that complement the Model S's luxury aesthetic.
                </p>
                <p className="text-gray-300 mb-6">
                  <strong className="text-cyan-400">Popular Model S wrap options:</strong> Deep gloss black, metallic silver, satin pearl white, carbon fiber accents.
                </p>
                <p className="text-gray-400 italic">
                  Starting at $3,200 for full color change wraps
                </p>
              </div>

              {/* Model X */}
              <div className="bg-gray-800/30 p-8 rounded-lg border border-gray-700">
                <h3 className="text-3xl font-bold text-white mb-4">Tesla Model X Wraps</h3>
                <p className="text-gray-300 mb-6">
                  The Model X's unique falcon-wing doors and large SUV body require expert installation. Our technicians specialize in wrapping complex surfaces, ensuring every panel, edge, and contour is covered flawlessly. Transform your Model X with a custom color that matches your style.
                </p>
                <p className="text-gray-300 mb-6">
                  <strong className="text-cyan-400">Popular Model X wrap options:</strong> Matte black, glossy red, satin dark grey, color-shifting wraps, chrome delete packages.
                </p>
                <p className="text-gray-400 italic">
                  Starting at $3,500 for full color change wraps
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Wrap + PPF Combination */}
        <section className="py-16 px-6 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-8 text-center">
              Tesla PPF + Wrap Protection Packages
            </h2>
            <p className="text-xl text-gray-300 mb-12 text-center max-w-3xl mx-auto">
              For maximum protection and style, combine Paint Protection Film (PPF) with a color change wrap. PPF shields high-impact areas like the front bumper, hood, and fenders from rock chips and scratches, while the wrap adds your custom color.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800/50 p-8 rounded-lg border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-white mb-4">PPF First, Then Wrap</h3>
                <p className="text-gray-300 mb-4">
                  We install clear Paint Protection Film on vulnerable areas (front bumper, hood, mirrors, rocker panels) to protect your Tesla's original paint. Then we apply a full color change wrap over the entire vehicle, including PPF-covered areas, for seamless appearance and double protection.
                </p>
                <p className="text-cyan-400 font-semibold">
                  Best for: Long-term protection + custom color
                </p>
              </div>
              <div className="bg-gray-800/50 p-8 rounded-lg border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-white mb-4">Wrap + PPF Overlay</h3>
                <p className="text-gray-300 mb-4">
                  Apply a full color change wrap first, then add clear PPF over high-impact zones for extra durability. This method protects your custom wrap color while maintaining a flawless finish. Ideal for Teslas driven in urban or highway conditions.
                </p>
                <p className="text-cyan-400 font-semibold">
                  Best for: Maximum wrap longevity + added defense
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Installation Process */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Our Tesla Wrap Installation Process
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-cyan-500 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Consultation</h3>
                <p className="text-gray-400">
                  Discuss your vision, choose colors and finishes, receive accurate quote
                </p>
              </div>
              <div className="text-center">
                <div className="bg-cyan-500 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Surface Prep</h3>
                <p className="text-gray-400">
                  Deep clean, decontaminate, and inspect every panel for perfect adhesion
                </p>
              </div>
              <div className="text-center">
                <div className="bg-cyan-500 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Precision Install</h3>
                <p className="text-gray-400">
                  Expert technicians apply wrap with precision patterning or bulk installation as needed, plus careful hand-finishing
                </p>
              </div>
              <div className="text-center">
                <div className="bg-cyan-500 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Quality Check</h3>
                <p className="text-gray-400">
                  Final inspection, post-heat treatment, care instructions provided
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-6 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Why Tesla Owners Trust Mr Wrap
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-cyan-400 mb-2">100+</div>
                <p className="text-gray-300">Teslas Wrapped</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-cyan-400 mb-2">5★</div>
                <p className="text-gray-300">Customer Rating</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-cyan-400 mb-2">3-5 Days</div>
                <p className="text-gray-300">Average Turnaround</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Tesla Wrap FAQs
            </h2>
            <div className="space-y-6">
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-3">How long does a Tesla wrap last?</h3>
                <p className="text-gray-300">
                  With proper care, a high-quality wrap can last 5-7 years. Factors like sun exposure, washing frequency, and maintenance affect longevity. We use premium vinyl designed for durability in San Diego's climate.
                </p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-3">Will wrapping my Tesla void the warranty?</h3>
                <p className="text-gray-300">
                  No. Wraps are a non-invasive cosmetic modification and do not affect Tesla's warranty. We use professional-grade materials and techniques that protect your paint rather than harm it.
                </p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-3">How much does it cost to wrap a Tesla?</h3>
                <p className="text-gray-300">
                  Full color change wraps start at $2,500 for Model 3, $2,800 for Model Y, $3,200 for Model S, and $3,500 for Model X. Final pricing depends on color choice, finish (matte, gloss, satin), and any additional customization like chrome delete or PPF protection.
                </p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-3">Can I wrap a leased Tesla?</h3>
                <p className="text-gray-300">
                  Absolutely! Wraps are fully removable and leave no damage to the original paint, making them perfect for leased Teslas. When your lease ends, we can remove the wrap and restore the vehicle to its factory condition.
                </p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-bold text-white mb-3">How do I care for my wrapped Tesla?</h3>
                <p className="text-gray-300">
                  Hand wash or use touchless car washes only. Avoid high-pressure washers and harsh chemicals. Park in shade when possible to extend wrap life. We provide detailed care instructions after installation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Embedded Quote Form */}
        <section className="py-16 px-6 bg-gray-950">
          <div className="max-w-2xl mx-auto">
            <EmbeddedQuoteForm
              defaultService="vehicle-wrap"
              heading="Get a Free Tesla Wrap Quote"
              subheading="Tell us your Tesla model, year, and the color or finish you have in mind. We'll respond with a personalized quote from our Vista team."
            />
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 px-6 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Related Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link
                to="/car-wrap-san-diego"
                className="bg-gray-800/50 p-6 rounded-lg border border-cyan-500/20 hover:border-cyan-500 transition text-center"
              >
                <h3 className="text-xl font-bold text-cyan-400 mb-2">Car Wraps San Diego</h3>
                <p className="text-gray-400">Professional vehicle wrapping services for all makes and models</p>
              </Link>
              <Link
                to="/colored-ppf-san-diego"
                className="bg-gray-800/50 p-6 rounded-lg border border-cyan-500/20 hover:border-cyan-500 transition text-center"
              >
                <h3 className="text-xl font-bold text-cyan-400 mb-2">Colored PPF San Diego</h3>
                <p className="text-gray-400">Color change paint protection film with self-healing protection and premium finishes</p>
              </Link>
              <Link
                to="/chrome-delete"
                className="bg-gray-800/50 p-6 rounded-lg border border-cyan-500/20 hover:border-cyan-500 transition text-center"
              >
                <h3 className="text-xl font-bold text-cyan-400 mb-2">Chrome Delete</h3>
                <p className="text-gray-400">Remove chrome trim with vinyl wrap for a modern look</p>
              </Link>
              <Link
                to="/window-tinting"
                className="bg-gray-800/50 p-6 rounded-lg border border-cyan-500/20 hover:border-cyan-500 transition text-center"
              >
                <h3 className="text-xl font-bold text-cyan-400 mb-2">Window Tinting</h3>
                <p className="text-gray-400">Ceramic window tint for UV protection and heat reduction</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Service Area */}
        <section className="py-12 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-6">
              Serving San Diego & North County
            </h2>
            <p className="text-gray-400 mb-4">
              Vista • Carlsbad • Oceanside • Encinitas • San Marcos • Escondido • Rancho Santa Fe • Del Mar • La Jolla • Poway • Solana Beach
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-6 bg-gradient-to-r from-cyan-600 to-blue-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Tesla?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get a free consultation and quote today. Call 858-282-8119 or request a quote online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:858-282-8119"
                className="bg-white text-cyan-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-lg transition text-lg"
              >
                Call Now
              </a>
              <Link
                to="/contact"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg transition text-lg"
              >
                Get Free Quote
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
